import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Users,
  CreditCard,
  FileText,
  Network,
  Package,
  ScrollText,
  Menu,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Admin console shell — nested route layout that wraps every `/admin/*` page.
 *
 * The authenticated chrome (iCrop header, signed-in email, sign-out) is already
 * provided by `RootLayout` → `Layout`; this component only adds the admin
 * sidebar (desktop) / drawer (mobile) and renders the active page via Outlet.
 */

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** Match the path exactly (used for the index route). */
  end?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/workspaces', label: 'Workspaces', icon: Building2 },
  { to: '/admin/users', label: 'Users', icon: Users },
  { to: '/admin/plans', label: 'Plans', icon: CreditCard },
  { to: '/admin/doctypes', label: 'Doctypes', icon: FileText },
  { to: '/admin/org-types', label: 'Operation Types', icon: Network },
  { to: '/admin/products', label: 'Products', icon: Package },
  { to: '/admin/audit-log', label: 'Audit Log', icon: ScrollText },
];

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav className="space-y-1" aria-label="Admin console">
      {NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )
            }
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

export function AdminConsoleLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
      {/* Mobile: drawer toggle */}
      <div className="flex items-center justify-between lg:hidden">
        <h2 className="text-lg font-semibold">Admin Console</h2>
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium"
          aria-label="Open admin menu"
        >
          <Menu className="h-4 w-4" />
          Menu
        </button>
      </div>

      {/* Desktop: persistent sidebar */}
      <aside className="hidden w-56 shrink-0 lg:block">
        <div className="sticky top-6">
          <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Admin Console
          </p>
          <NavList />
        </div>
      </aside>

      {/* Mobile: slide-in drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute left-0 top-0 h-full w-64 border-r bg-card p-4 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Admin Console
              </p>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close admin menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <NavList onNavigate={() => setDrawerOpen(false)} />
          </div>
        </div>
      )}

      {/* Active page */}
      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}
